import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { normalizeText } from '../utils/dataUtils';
import { ContactsService } from '../services/contactsService';
import '../styles/pages/contacts.css';

const CONTACT_TYPES = ['Intern', 'Community', 'Provider', 'Other'];

const TYPE_LABELS = {
    Intern: 'Interno',
    Community: 'Comunidad',
    Provider: 'Proveedor',
    Other: 'Otro'
};

const EMPTY_FORM = {
    fullName: '',
    phone: '',
    phone2: '',
    address: '',
    contactType: 'Other',
    email: ''
};

const TYPE_COLORS = {
    Intern: '#3b82f6',
    Community: '#10b981',
    Provider: '#f59e0b',
    Other: '#8b5cf6'
};

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function Contacts() {
    const { authorizedFetch } = useAuth();

    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    // Modal state
    const [showForm, setShowForm] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [showPhone2, setShowPhone2] = useState(false);
    const [saving, setSaving] = useState(false);

    // Detail modal
    const [selectedContact, setSelectedContact] = useState(null);

    // Delete confirm
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    useEffect(() => {
        fetchContacts();
    }, [authorizedFetch]);

    const fetchContacts = async () => {
        if (!authorizedFetch) return;
        setLoading(true);
        setError('');
        try {
            const data = await ContactsService.getAllContacts(authorizedFetch);
            setContacts(data);
        } catch (err) {
            setError('Error al cargar contactos');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Filter contacts by search
    const filtered = contacts.filter(c => {
        const q = normalizeText(search);
        return (
            normalizeText(c.fullName).includes(q) ||
            c.phone.includes(q) ||
            normalizeText(c.email).includes(q) ||
            normalizeText(c.contactType).includes(q)
        );
    });

    // Form handlers
    const openNewContact = () => {
        setEditingContact(null);
        setForm(EMPTY_FORM);
        setShowPhone2(false);
        setShowForm(true);
    };

    const openEditContact = (contact) => {
        setEditingContact(contact);
        setForm({
            fullName: contact.fullName,
            phone: contact.phone,
            phone2: contact.phone2 || '',
            address: contact.address || '',
            contactType: contact.contactType,
            email: contact.email || ''
        });
        setShowPhone2(!!contact.phone2);
        setSelectedContact(null);
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingContact(null);
        setForm(EMPTY_FORM);
        setShowPhone2(false);
    };

    const handleSave = async () => {
        if (!form.fullName.trim() || !form.phone.trim()) return;
        setSaving(true);
        try {
            if (editingContact) {
                const updated = await ContactsService.updateContact(authorizedFetch, editingContact._id, form);
                setContacts(prev => prev.map(c => c._id === updated._id ? updated : c));
            } else {
                const created = await ContactsService.createContact(authorizedFetch, form);
                setContacts(prev => [...prev, created].sort((a, b) => a.fullName.localeCompare(b.fullName)));
            }
            closeForm();
        } catch (err) {
            console.error('Error saving contact:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await ContactsService.deleteContact(authorizedFetch, id);
            setContacts(prev => prev.filter(c => c._id !== id));
            setDeleteConfirmId(null);
            setSelectedContact(null);
        } catch (err) {
            console.error('Error deleting contact:', err);
            setError(err.message || 'Error al eliminar contacto');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <>
            <Navigation />
            <div className="contacts-container">
                <div className="contacts-header">
                    <div className="contacts-title-row">
                        <h1>📇 Contactos</h1>
                        <button className="add-contact-btn" onClick={openNewContact}>+ Nuevo Contacto</button>
                    </div>
                    <div className="contacts-search">
                        <input
                            type="text"
                            placeholder="Buscar por nombre, teléfono, email o tipo..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && (
                            <button className="clear-search" onClick={() => setSearch('')}>✕</button>
                        )}
                    </div>
                </div>

                {error && <div className="contacts-error">{error}</div>}

                {loading ? (
                    <div className="contacts-loading">Cargando contactos...</div>
                ) : filtered.length === 0 ? (
                    <div className="contacts-empty">
                        {search ? 'No se encontraron contactos.' : 'No hay contactos aún. ¡Agrega uno!'}
                    </div>
                ) : (
                    <div className="contacts-list">
                        {filtered.map(contact => (
                            <div
                                key={contact._id}
                                className="contact-card"
                                onClick={() => setSelectedContact(contact)}
                            >
                                <div className="contact-card-left">
                                    <div
                                        className="contact-initials"
                                        style={{ backgroundColor: TYPE_COLORS[contact.contactType] || TYPE_COLORS.Other }}
                                    >
                                        {getInitials(contact.fullName)}
                                    </div>
                                    <div className="contact-card-info">
                                        <h4>{contact.fullName}</h4>
                                        <span className="contact-phone">{contact.phone}</span>
                                        {contact.email && <span className="contact-email">{contact.email}</span>}
                                    </div>
                                </div>
                                <div className="contact-card-right">
                                    <span
                                        className="contact-type-label"
                                        style={{ color: TYPE_COLORS[contact.contactType] || TYPE_COLORS.Other }}
                                    >
                                        {TYPE_LABELS[contact.contactType] || contact.contactType}
                                    </span>
                                    <span className="contact-arrow">→</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Detail Modal */}
                {selectedContact && (
                    <div className="contact-modal-overlay" onClick={() => { setSelectedContact(null); setDeleteConfirmId(null); }}>
                        <div className="contact-modal" onClick={e => e.stopPropagation()}>
                            <div className="contact-modal-header">
                                <div
                                    className="contact-initials detail-size"
                                    style={{ backgroundColor: TYPE_COLORS[selectedContact.contactType] }}
                                >
                                    {getInitials(selectedContact.fullName)}
                                </div>
                                <div>
                                    <h2>{selectedContact.fullName}</h2>
                                    <span className="contact-type-tag" style={{ color: TYPE_COLORS[selectedContact.contactType] }}>
                                        {TYPE_LABELS[selectedContact.contactType] || selectedContact.contactType}
                                    </span>
                                </div>
                                <button className="contact-close-btn" onClick={() => { setSelectedContact(null); setDeleteConfirmId(null); }}>✕</button>
                            </div>

                            <div className="contact-detail-grid">
                                <div className="detail-item">
                                    <label>📞 Teléfono</label>
                                    <span>{selectedContact.phone}</span>
                                </div>
                                {selectedContact.phone2 && (
                                    <div className="detail-item">
                                        <label>📱 Teléfono 2</label>
                                        <span>{selectedContact.phone2}</span>
                                    </div>
                                )}
                                {selectedContact.email && (
                                    <div className="detail-item">
                                        <label>✉️ Email</label>
                                        <span>{selectedContact.email}</span>
                                    </div>
                                )}
                                {selectedContact.address && (
                                    <div className="detail-item full-width">
                                        <label>📍 Dirección</label>
                                        <span>{selectedContact.address}</span>
                                    </div>
                                )}
                            </div>

                            <div className="contact-detail-meta">
                                {selectedContact.createdBy && (
                                    <span>Creado por: {selectedContact.createdBy.username} • {formatDate(selectedContact.createdAt)}</span>
                                )}
                                {selectedContact.updatedBy && selectedContact.updatedAt !== selectedContact.createdAt && (
                                    <span>Editado por: {selectedContact.updatedBy.username} • {formatDate(selectedContact.updatedAt)}</span>
                                )}
                            </div>

                            <div className="contact-detail-actions">
                                <button className="contact-edit-btn" onClick={() => openEditContact(selectedContact)}>
                                    ✏️ Editar
                                </button>
                                {deleteConfirmId === selectedContact._id ? (
                                    <span className="contact-delete-confirm">
                                        <span>¿Eliminar?</span>
                                        <button className="confirm-yes-btn" onClick={() => handleDelete(selectedContact._id)}>Sí</button>
                                        <button className="confirm-no-btn" onClick={() => setDeleteConfirmId(null)}>No</button>
                                    </span>
                                ) : (
                                    <button className="contact-delete-btn" onClick={() => setDeleteConfirmId(selectedContact._id)}>
                                        🗑️ Eliminar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Create/Edit Form Modal */}
                {showForm && (
                    <div className="contact-modal-overlay" onClick={closeForm}>
                        <div className="contact-modal contact-form-modal" onClick={e => e.stopPropagation()}>
                            <h3>{editingContact ? '✏️ Editar Contacto' : '➕ Nuevo Contacto'}</h3>
                            <div className="contact-form">
                                <div className="form-row">
                                    <label>Nombre Completo *</label>
                                    <input
                                        type="text"
                                        value={form.fullName}
                                        onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
                                        placeholder="Nombre completo"
                                    />
                                </div>
                                <div className="form-row">
                                    <label>Teléfono *</label>
                                    <input
                                        type="tel"
                                        value={form.phone}
                                        onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                                        placeholder="Número de teléfono"
                                    />
                                </div>
                                {showPhone2 ? (
                                    <div className="form-row">
                                        <label>Teléfono 2</label>
                                        <input
                                            type="tel"
                                            value={form.phone2}
                                            onChange={e => setForm(p => ({ ...p, phone2: e.target.value }))}
                                            placeholder="Segundo número (opcional)"
                                        />
                                    </div>
                                ) : (
                                    <button className="add-phone2-btn" onClick={() => setShowPhone2(true)}>
                                        + Agregar segundo teléfono
                                    </button>
                                )}
                                <div className="form-row">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                                        placeholder="correo@ejemplo.com"
                                    />
                                </div>
                                <div className="form-row">
                                    <label>Dirección</label>
                                    <input
                                        type="text"
                                        value={form.address}
                                        onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                                        placeholder="Dirección"
                                    />
                                </div>
                                <div className="form-row">
                                    <label>Tipo de Contacto *</label>
                                    <select
                                        value={form.contactType}
                                        onChange={e => setForm(p => ({ ...p, contactType: e.target.value }))}
                                    >
                                        {CONTACT_TYPES.map(type => (
                                            <option key={type} value={type}>{TYPE_ICONS[type]} {type}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="contact-form-actions">
                                <button className="contact-cancel-btn" onClick={closeForm}>Cancelar</button>
                                <button
                                    className="contact-save-btn"
                                    onClick={handleSave}
                                    disabled={saving || !form.fullName.trim() || !form.phone.trim()}
                                >
                                    {saving ? 'Guardando...' : 'Guardar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
